import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, API_BASE, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download, Trash2, Beaker, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSiteSettings } from "@/lib/settings";

export default function PatientPrescriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();
  const { platform } = useSiteSettings();
  const marketplaceOn = platform?.marketplace_enabled !== false;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prescriptions");
      setItems(r.data || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load prescriptions"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this prescription? This cannot be undone.")) return;
    setBusy(id);
    try {
      await api.delete(`/prescriptions/${id}`);
      toast.success("Prescription deleted");
      setItems((x) => x.filter((p) => p.id !== id));
    } catch (e) { toast.error(apiError(e, "Delete failed")); }
    finally { setBusy(null); }
  };

  const download = (id) => {
    const token = localStorage.getItem("efgh_token");
    // Use query-token auth so browsers can open the file directly
    window.open(`${API_BASE}/prescriptions/${id}/file?auth=${token}`, "_blank");
  };

  const bookThese = (rx) => {
    // Marketplace opens with the first matched test as a search query hint
    const hint = (rx.matched_tests || [])[0]?.name || (rx.extracted_names || [])[0] || "";
    nav(`/book-tests${hint ? `?q=${encodeURIComponent(hint)}` : ""}`);
  };

  return (
    <DashboardLayout role="patient">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
            <FileText className="w-6 h-6 text-efgh-orange" /> Prescription history
          </h1>
          <p className="text-ink/60 mt-1">Every doctor&apos;s prescription you&apos;ve uploaded, with the tests our AI recommended.</p>
        </div>
        <Button data-testid="rx-upload-cta" onClick={() => nav("/book-tests")} className="bg-ink hover:bg-ink/90 text-white">Upload new prescription</Button>
      </div>

      <div className="mt-6 grid gap-3">
        {loading && (
          <Card className="p-10 rounded-2xl text-center text-ink/50">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          </Card>
        )}
        {!loading && items.length === 0 && (
          <Card className="p-10 rounded-2xl text-center">
            <FileText className="w-10 h-10 mx-auto text-ink/30" />
            <div className="mt-3 font-heading font-semibold text-ink">No prescriptions yet</div>
            <p className="text-sm text-ink/60 mt-1">Upload a prescription and we&apos;ll auto-recommend the right tests.</p>
            <Button onClick={() => nav("/book-tests")} className="mt-4 bg-ink hover:bg-ink/90 text-white">Upload prescription</Button>
          </Card>
        )}
        {items.map((rx) => (
          <Card key={rx.id} className="p-5 rounded-2xl" data-testid={`rx-${rx.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-widest text-ink/50">Rx · {rx.id.slice(0, 8).toUpperCase()}</div>
                <div className="mt-1 font-heading font-semibold text-ink">
                  {rx.doctor_name ? `Dr. ${rx.doctor_name}` : "Prescription"}
                  <span className="text-xs text-ink/50 font-normal ml-2">· {new Date(rx.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm text-ink/60 mt-1">
                  Found {rx.extracted_names?.length || 0} tests · Matched {rx.matched_tests?.length || 0} in catalogue
                </div>
                {(rx.matched_tests || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {rx.matched_tests.slice(0, 4).map((t) => (
                      <span key={t.id} className="pill bg-efgh-green/10 text-efgh-green text-[11px]"><Beaker className="w-2.5 h-2.5" /> {t.name}</span>
                    ))}
                    {rx.matched_tests.length > 4 && <span className="pill bg-muted text-ink/50 text-[11px]">+{rx.matched_tests.length - 4} more</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid={`rx-view-${rx.id}`} onClick={() => setDetail(rx)}>View</Button>
                {rx.file_path && <Button variant="outline" size="sm" data-testid={`rx-dl-${rx.id}`} onClick={() => download(rx.id)}><Download className="w-3.5 h-3.5" /></Button>}
                {marketplaceOn && (
                  <Button size="sm" data-testid={`rx-book-${rx.id}`} onClick={() => bookThese(rx)} className="bg-ink hover:bg-ink/90 text-white">
                    Book <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                )}
                <Button variant="outline" size="sm" disabled={busy === rx.id} onClick={() => remove(rx.id)} data-testid={`rx-del-${rx.id}`}>
                  {busy === rx.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <RxDetailDialog rx={detail} onClose={() => setDetail(null)} onBook={bookThese} onDownload={download} marketplaceOn={marketplaceOn} />
    </DashboardLayout>
  );
}

function RxDetailDialog({ rx, onClose, onBook, onDownload, marketplaceOn = true }) {
  if (!rx) return null;
  return (
    <Dialog open={!!rx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="rx-detail-dialog">
        <DialogTitle className="font-heading">Prescription details</DialogTitle>
        <div className="text-sm text-ink/60">Uploaded {new Date(rx.created_at).toLocaleString()}</div>

        {rx.doctor_name && <div className="mt-3 text-sm"><span className="text-ink/50">Doctor:</span> <b>{rx.doctor_name}</b></div>}
        {rx.notes && <div className="mt-1 text-sm text-ink/70">{rx.notes}</div>}

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-ink/50 font-heading font-semibold">Matched tests ({rx.matched_tests?.length || 0})</div>
          {(rx.matched_tests || []).length === 0 ? (
            <div className="mt-2 text-sm text-ink/50">None matched our catalogue.</div>
          ) : (
            <div className="mt-2 space-y-2">
              {rx.matched_tests.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border">
                  <div className="text-sm text-ink">{t.name}</div>
                  <div className="text-xs text-efgh-green">₹{t.discounted_price || t.price}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {(rx.unmatched_names || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest text-ink/50 font-heading font-semibold">Not in catalogue</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {rx.unmatched_names.map((n, i) => <span key={i} className="pill bg-muted text-ink/60 text-[11px]">{n}</span>)}
            </div>
          </div>
        )}

        <div className="mt-5 flex gap-2 flex-wrap">
          {rx.file_path && <Button variant="outline" onClick={() => onDownload(rx.id)}><Download className="w-4 h-4 mr-2" /> Original</Button>}
          {marketplaceOn && (
            <Button className="bg-ink hover:bg-ink/90 text-white ml-auto" onClick={() => { onClose(); onBook(rx); }}>
              Book these tests <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
